import { useEffect, useState } from "react";

import { unusedPageCheck } from "../connection/cloudWorkspaceIdle.logic";

const USE_EVENTS = ["pointerdown", "keydown", "wheel", "touchstart"] as const;

/** When this page was last used: input, focus, or becoming visible. One record for the page. */
let lastUsedAt = Date.now();
const usedListeners = new Set<() => void>();
let tracking = false;

const used = () => {
  lastUsedAt = Date.now();
  for (const listener of usedListeners) listener();
};

const track = () => {
  if (tracking || typeof window === "undefined") return;
  tracking = true;
  for (const type of USE_EVENTS)
    window.addEventListener(type, used, { capture: true, passive: true });
  window.addEventListener("focus", used);
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") used();
  });
};

/**
 * Whether nobody has used this page for the unused window, right now. For code outside React that
 * must not act for a page nobody is at, such as opening a Cloud Workspace connection, which starts
 * a stopped workspace.
 */
export function isPageUnused(): boolean {
  track();
  return unusedPageCheck({ lastUsedAt, now: Date.now() }).unused;
}

/**
 * Whether nobody has used this page for the unused window. Input only moves a timestamp, so a
 * burst of events never re-renders; state changes when the page crosses into or out of unused. One
 * timer at a time, armed for the moment the window could next run out.
 */
export function usePageUnused(): boolean {
  const [unused, setUnused] = useState(false);
  useEffect(() => {
    track();
    let timer: number | undefined;
    const check = () => {
      const result = unusedPageCheck({ lastUsedAt, now: Date.now() });
      if (result.unused) {
        timer = undefined;
        setUnused(true);
        return;
      }
      timer = window.setTimeout(check, result.checkInMs);
    };
    const onUsed = () => {
      if (timer !== undefined) return;
      setUnused(false);
      check();
    };
    usedListeners.add(onUsed);
    check();
    return () => {
      window.clearTimeout(timer);
      usedListeners.delete(onUsed);
    };
  }, []);
  return unused;
}
